import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useSelector } from 'Hooks';
import useI18n from 'i18n';
import times from 'lodash/times';
import StampCard from './StampCard';
import StampCardDetails from './StampCardDetails';
import Dialog from '../../../../Components/Dialog';
import {
  userStampsQuery_user_stampCards,
  userStampsQuery_user_stamps,
} from 'Components/App/__queries__';

const Wrapper = styled.div`
  padding: 24px 0 0 0;
  background: #f8f8fb;
  animation: fade-in 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const Inner = styled.div`
  @media (min-width: 920px) {
    display: flex;
    flex-wrap: wrap;
  }
`;

interface Props {
  homeRef: any;
}

/**
 * Home page StampCard Index component
 */
const StampCardsIndex = ({ homeRef }: Props) => {
  const { i18n } = useI18n();
  const [stampCards, setStampCards] = useState<userStampsQuery_user_stampCards[]>([]);
  const [stamps, setStamps] = useState<userStampsQuery_user_stamps[]>([]);
  const [activeStampCardId, setActiveStampCardId] = useState<string>('');
  const [activeStampCard, setActiveCard] = useState<userStampsQuery_user_stampCards | null>(
    null,
  );
  const [activeStamps, setActiveStamps] = useState<boolean[] | undefined>(undefined);

  const venue = useSelector((store) => store.venue);
  const userStampCards = useSelector((state) => state.userStampCards);
  const userStamps = useSelector((state) => state.userStamps);

  const venueFilter = (card: userStampsQuery_user_stampCards) => {
    return card.venues.length === 0 || (venue && card.venues.map((v) => v.id).includes(venue.id));
  };

  useEffect(() => {
    setStampCards(userStampCards.filter(venueFilter));
    setStamps(userStamps);
  }, [userStampCards, userStamps, venue]);

  useEffect(() => {
    if (!activeStampCardId) {
      return;
    }

    const activeCard = stampCards.find((card) => card.id === activeStampCardId);

    if (!activeCard) {
      return;
    }

    setActiveCard(activeCard);

    const activeStamps = times(activeCard.count, (i) => i < getStampsById(activeCard.id).length);

    if (activeStamps) {
      setActiveStamps(activeStamps);
    }
  }, [activeStampCardId]);

  useEffect(() => {
    if (!homeRef?.current) return;

    if (activeStampCardId) {
      homeRef.current.classList.add('modal-open');
    } else {
      setTimeout(() => {
        homeRef?.current?.classList.remove('modal-open');
      }, 400);
    }
  }, [activeStampCardId]);

  const getStampsById = useCallback(
    (id) => {
      return stamps.filter((stamp) => stamp.stampCard.id === id) || [];
    },
    [stamps],
  );

  if (!stampCards || stampCards.length === 0) {
    return null;
  }

  const onSetActiveStampCardId = (id: string) => {
    setActiveStampCardId(id);
  };

  return (
    <Wrapper>
      <Inner>
        {stampCards.map((card) => (
          <StampCard
            key={card.id}
            cardId={card.id}
            stamps={getStampsById(card.id)}
            requiredCount={card.count}
            name={i18n(card.nameLang)}
            description={i18n(card.descriptionLang)}
            onSetActiveStampCardId={onSetActiveStampCardId}
          />
        ))}
        <Dialog visible={Boolean(activeStampCardId)} onClose={() => onSetActiveStampCardId('')}>
          <StampCardDetails
            name={i18n(activeStampCard?.nameLang)}
            description={i18n(activeStampCard?.descriptionLang)}
            stamps={activeStamps || []}
            onSetActiveStampCardId={onSetActiveStampCardId}
          />
        </Dialog>
      </Inner>
    </Wrapper>
  );
};

export default StampCardsIndex;
